<template>
  <div class="chart-container">
    <Chart type="bar" :data="chartData" :options="chartOptions" />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, watch } from "vue";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from "chart.js";
import { Chart } from "vue-chartjs";
import annotationPlugin from "chartjs-plugin-annotation";
import type { ChartData, ChartOptions, LinearScaleOptions } from "chart.js";

// 必要な要素の登録
ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
  annotationPlugin
);

export default defineComponent({
  name: "HyoshoChartComponent",
  components: {
    Chart,
  },
  props: {
    tonenChartData: {
      type: Array as PropType<number[]>,
    },
    zennenChartData: {
      type: Array as PropType<number[]>,
    },
    ruikeiChartData: {
      type: Array as PropType<number[]>,
    },
  },
  setup(props) {
    const chartData: ChartData<"line" | "bar"> = {
      labels: [
        "8月",
        "9月",
        "10月",
        "11月",
        "12月",
        "1月",
        "2月",
        "3月",
        "4月",
        "5月",
        "6月",
        "7月",
      ],
      datasets: [
        {
          type: "line",
          label: "累計増加実績",
          data: props.ruikeiChartData == undefined ? [] : props.ruikeiChartData,
          borderColor: "#A8C5E5",
          backgroundColor: "#A8C5E5",
          borderWidth: 2,
          fill: false,
          yAxisID: "y-left", // 右側のスケールを使用
        },
        {
          type: "bar",
          label: "今年",
          data: props.tonenChartData == undefined ? [] : props.tonenChartData,
          backgroundColor: "#0068B7",
          yAxisID: "y-right", // 右側のスケールを使用
        },
        {
          type: "bar",
          label: "昨年",
          data: props.zennenChartData == undefined ? [] : props.zennenChartData,
          backgroundColor: "#c8d2db",
          yAxisID: "y-right", // 右側のスケールを使用
        },
      ],
    };

    // オプションの設定
    const chartOptions: ChartOptions<"line" | "bar"> = {
      responsive: true,
      plugins: {
        legend: {
          position: "top",
        },
        annotation: {
          // annotations はオブジェクトの配列である必要があります
          annotations: [
            {
              type: "line",
              yScaleID: "y-right", // Y軸を指定
              yMin: 0, // X軸の最小値を指定
              yMax: 0, // X軸の最大値を指定（X軸全体に渡る線）
              value: 0, // Y軸の値を0に設定（ゼロライン）
              borderColor: "red",
              borderWidth: 2,
            },
          ],
        },
      },
      scales: {
        "y-right": {
          type: "linear",
          position: "right", // 折れ線グラフの目盛りを左側に表示
          ticks: {
            beginAtZero: true, // 目盛りの開始をゼロに設定
            font: {
              family: "monospace", // 等幅フォントを使用して目盛りを表示
            },
            callback: function (value: number) {
              const formattedValue = value.toLocaleString(); // カンマ区切りにフォーマット
              return formattedValue.padEnd(11, " "); // 11桁になるまで空白パディング
            },
          },
          title: {
            display: true,
            text: "実績", // 右側軸のラベルをここに追加
            color: "#000", // ラベルの色
            font: {
              size: 14, // ラベルのフォントサイズ
              weight: "bold", // ラベルを太字にする
            },
          },
        } as unknown as LinearScaleOptions, // unknownにキャストしてからLinearScaleOptionsにキャスト
        "y-left": {
          type: "linear",
          position: "left", // 棒グラフの目盛りを右側に表示
          ticks: {
            beginAtZero: true, // 目盛りの開始をゼロに設定
            font: {
              family: "monospace", // 等幅フォントを使用して目盛りを表示
            },
            callback: function (value: number) {
              const formattedValue = value.toLocaleString(); // カンマ区切りにフォーマット
              return formattedValue.padStart(11, " "); // 11桁になるまで空白パディング
            },
          },
          grid: {
            drawOnChartArea: false, // グラフエリアに線を表示しない
          },
          title: {
            display: true,
            text: "累計増加実績", // 右側軸のラベルをここに追加
            color: "#000", // ラベルの色
            font: {
              size: 14, // ラベルのフォントサイズ
              weight: "bold", // ラベルを太字にする
            },
          },
        } as unknown as LinearScaleOptions, // unknownにキャストしてからLinearScaleOptionsにキャスト
      },
    };

    // ruikeiChartData の変更を監視
    watch(
      () => props.ruikeiChartData,
      (newData) => {
        if (newData) {
          chartData.datasets[0].data = newData; // データを更新
        }
      },
      { immediate: true, deep: true } // 初期化時も実行, deep でネストされた変更も監視
    );

    // tonenChartData の変更を監視
    watch(
      () => props.tonenChartData,
      (newData) => {
        if (newData) {
          chartData.datasets[1].data = newData; // データを更新
        }
      },
      { immediate: true, deep: true } // 初期化時も実行, deep でネストされた変更も監視
    );

    // zennenChartData の変更を監視
    watch(
      () => props.zennenChartData,
      (newData) => {
        if (newData) {
          chartData.datasets[2].data = newData; // データを更新
        }
      },
      { immediate: true, deep: true } // 初期化時も実行, deep でネストされた変更も監視
    );

    return {
      chartData,
      chartOptions,
    };
  },
});
</script>

<style>
.chart-container {
  width: auto; /* 例えば画面全体の幅を使用 */
}
</style>
